// Global packages and components
import { clsx } from 'clsx'
import { Link } from '@remix-run/react'
import { usePagination, LEFT_DOTS, RIGHT_DOTS } from '~/hooks'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

// Types
import type { ReactNode } from 'react'

export interface PaginationProps {
  totalCount: number
  activePage: number
  pageSize: number
  siblingCount?: number
  showPrevNext?: boolean
  /**
   * Renders real anchors. Listings pass this so a non rendering crawler can
   * follow pagination into the product layer, which buttons never allowed.
   */
  getPageHref?: (page: number) => string
  /** Button mode, still used where pagination is not a navigation */
  onChange?: (e: number) => void
  preventScrollReset?: boolean
  label?: string
}

const buttonStyles =
  'flex size-8 items-center justify-center rounded-full border border-teal-green text-sm lg:size-10 lg:text-base'

// Main export
const Pagination = ({
  totalCount,
  activePage,
  pageSize,
  siblingCount = 1,
  showPrevNext = true,
  getPageHref,
  onChange,
  preventScrollReset = true,
  label = 'Pagination',
}: PaginationProps) => {
  const paginationRange = usePagination({
    activePage,
    totalCount,
    siblingCount,
    pageSize,
  })

  if (activePage === 0 || !paginationRange || paginationRange.length < 2) {
    return null
  }

  const lastPage = paginationRange[paginationRange.length - 1]

  // One control, rendered as a link where the listing gave us an href builder
  // and as a button everywhere else
  const PageControl = ({
    page,
    className,
    ariaLabel,
    rel,
    isCurrent,
    children,
  }: {
    page: number
    className: string
    ariaLabel?: string
    rel?: string
    isCurrent?: boolean
    children: ReactNode
  }) =>
    getPageHref ? (
      <Link
        to={getPageHref(page)}
        rel={rel}
        aria-label={ariaLabel}
        aria-current={isCurrent ? 'page' : undefined}
        preventScrollReset={preventScrollReset}
        className={className}
      >
        {children}
      </Link>
    ) : (
      <button
        type="button"
        onClick={() => onChange?.(page)}
        aria-label={ariaLabel}
        aria-current={isCurrent ? 'page' : undefined}
        className={className}
      >
        {children}
      </button>
    )

  return (
    <nav aria-label={label}>
      <ul className="-mx-1 flex items-center justify-center">
        {activePage !== 1 && showPrevNext && (
          <li className="px-1">
            <PageControl
              page={activePage - 1}
              rel="prev"
              ariaLabel="Previous page"
              className="flex size-8 items-center justify-center rounded-full border border-teal-green lg:size-10"
            >
              <ChevronLeftIcon className="size-5" />
            </PageControl>
          </li>
        )}
        {paginationRange.map(pageNumber => {
          // Need to distinguish between ellipses
          if (pageNumber === LEFT_DOTS || pageNumber === RIGHT_DOTS) {
            return (
              <li
                className="px-1 text-sm lg:text-base"
                key={pageNumber}
              >
                &#8230;
              </li>
            )
          }

          const isCurrent = pageNumber === activePage

          return (
            <li
              key={pageNumber}
              className="px-1"
            >
              <PageControl
                page={Number(pageNumber)}
                isCurrent={isCurrent}
                ariaLabel={`Page ${pageNumber}`}
                className={clsx(buttonStyles, {
                  'bg-teal-green font-semibold text-white': isCurrent,
                  'bg-transparent': !isCurrent,
                })}
              >
                {pageNumber}
              </PageControl>
            </li>
          )
        })}
        {activePage !== lastPage && showPrevNext && (
          <li className="px-1">
            <PageControl
              page={activePage + 1}
              rel="next"
              ariaLabel="Next page"
              className="flex size-8 items-center justify-center rounded-full lg:size-10"
            >
              <ChevronRightIcon className="size-5" />
            </PageControl>
          </li>
        )}
      </ul>
    </nav>
  )
}

export default Pagination
